<template>
  <div ref="wrapperRef" class="folders-files-pages-grid h-full overflow-auto">
    <div v-if="!!activeFile" class="h-full flex flex-col">
      <!-- File details grid view -->
      <div v-if="activeFile.content_thumbnails?.length" class="bg-base overflow-auto flex flex-col flex-auto">
        <div v-if="isDragNDropEnabled" class="flex justify-end items-center color-primary bg-base pa-2 min-h-11">
          <CSectionSelectionActions
            :added-item-ids="addedItems.map((item) => item.id)"
            :selected-item-ids="selectedItems.map((item) => item.id)"
            :total-selected="selectedItems?.length || 0"
            @clear-selection="selectedItems = []"
            @items-added="(_, position) => addItems(position)"
            @items-removed="(_) => deleteItems()"
          />
        </div>

        <NSpace class="pa-4 overflow-auto" size="large">
          <GridItem
            v-for="(thumbnail, idx) in activeFile.content_thumbnails"
            :key="thumbnail"
            class="border-solid border-primary6"
            :is-selected="selectedPages.some((page) => page.file.id === activeFile?.id && page.page_index === idx)"
            :item="{
              file: { id: activeFile.id, name: activeFile.name },
              id: createPageId(activeFile.id, idx),
              name: `${t('canvasUI.contentSelectorApp.foldersFilesList.page')} ${idx + 1}`,
              page_index: idx,
              permissions: activeFile.permissions,
              thumbnail_url: thumbnail,
              type: 'page',
              metadata: activeFile.metadata,
            }"
            :movable="isDragNDropEnabled"
            :selectedIds="(isDragNDropEnabled ? selectedItems : addedItems).map((f) => f.id)"
            @item-clicked="api.fileOpener?.({ fileId: getFileId(activeFile), pageIndex: idx })"
            @item-deselected="deselectPage"
            @item-selected="handleSelectItem"
          />
        </NSpace>
      </div>

      <div v-else class="h-full w-full flex items-center justify-center" :style="{ color: themeVars.primary3 }">
        <div class="flex flex-col gap-3 items-center justify-center max-w-[400px] text-center">
          <CIcon icon="circle-exclamation" size="36px" />
          <div class="font-size-[20px]">
            {{ t('canvasUI.contentSelectorApp.foldersFiles.thumbnailsAreMissing') }}
          </div>
          <div class="font-size-[14px]">
            {{ t('canvasUI.contentSelectorApp.foldersFiles.pdfReuploadHint') }}
          </div>
        </div>
      </div>
    </div>
    <template v-else>
      <!-- Files list -->
      <div class="files-grid h-full">
        <div ref="toolbarWrapperRef" class="flex flex-col gap-3">
          <CSearchFilters
            ref="searchFiltersRef"
            v-model:filters="metadataFilters"
            :content-type="appProps?.onlyAllowContentPdfPages?.value ? [FileContentTypeEnum.PDF] : undefined"
            dependent-filters-entity="file"
            enable-dependent-filters
            entity="file"
            :hide-filters="selectedSection !== 'all'"
            :initial-search-value="search"
            :placeholder="t('canvasUI.canvasBuilder.sectionSelector.searchByNameOrTag')"
            v-bind="popularityProps"
            @update:filters="fetchFilesAfterFiltering"
            @update:search="handleSearch"
          >
            <template v-if="!disabledSearchContext" #input-suffix>
              <div
                v-if="selectedSection === 'all'"
                class="flex items-center justify-center min-h-[36px] pr-3 mr-1 border-r border-r-solid border-primary4"
              >
                <CButton
                  class="rounded-r-0 border-r-0"
                  size="tiny"
                  :type="isLocalSearch ? 'primary' : 'default'"
                  @click="() => toggleSearchMode(true)"
                >
                  {{ t('canvasUI.browserApp.content.localSearch') }}
                </CButton>
                <CButton
                  class="rounded-l-0"
                  size="tiny"
                  :type="isLocalSearch ? 'default' : 'primary'"
                  @click="() => toggleSearchMode(false)"
                >
                  {{ t('canvasUI.browserApp.content.globalSearch') }}
                </CButton>
              </div>
            </template>
          </CSearchFilters>

          <ContentGridToolbar
            v-if="!isFetchingContent"
            :are-all-selected="areAllSelected"
            :are-some-selected="areSomeSelected"
            class="rounded-t-lg"
            :total-items="filteredDisplayedFiles.filter((item: any) => item.type === 'file').length"
            @toggle-select-all="toggleSelectAll"
          />
        </div>
        <CFullScreenLoader v-if="isFetchingContent" class="bg-base" fit-parent :style="{ height: wrapperHeight }" />
        <CList
          v-if="!!filteredDisplayedFiles?.length && !isFetchingContent"
          class="rounded-b-lg flex bg-base overflow-y-auto"
          :data="filteredDisplayedFiles"
          :height="parseInt(wrapperHeight)"
          :maxRowHeight="200"
          :movable="isDragNDropEnabled"
          :selectedIds="selectedFiles.map((f) => f.id)"
          @item:click="handleSelectItem"
        >
          <template #item="{ item, index, isContextMenuOpen, isSelected }">
            <FoldersFilesListItem
              :data="filteredDisplayedFiles"
              :do-not-show-folder-details="selectedSection === 'favorites'"
              :filters="computed(() => ({}) as Record<string, any>)"
              :hide-files-count="appProps?.disableNoPptxData?.value"
              :highlight-text="search"
              :index="index"
              :is-context-menu-open="isContextMenuOpen"
              :is-disabled="
                isItemDisabled(
                  item as SelectedFile | SelectedFolder | RecentFileWithFavoriteId | FolderFileWithFolderId,
                )
              "
              :is-selected="isSelected"
              :item="getTypedItem(item)"
              :metadata="processedMetadata[item.id] || {}"
              :selected-items="selectedFiles"
              :style="{ opacity: item.opacity || 1 }"
              @expand-file="expandFile"
              @open-folder="openFolder"
              @preview-file="previewFile"
            />
          </template>
        </CList>
        <div
          v-else-if="!isFetchingContent"
          class="w-full flex items-center justify-center rounded-b-lg bg-base"
          :style="{ color: themeVars.primary3, height: wrapperHeight }"
        >
          <div class="flex flex-col gap-3 items-center justify-center max-w-[400px] text-center">
            <CIcon icon="circle-exclamation" size="36px" />
            <div class="font-size-[20px]">
              {{ t('canvasUI.contentSelectorApp.foldersFilesList.noFilesOrFolders') }}
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { isEmpty } from 'lodash-es'
import { NSpace } from 'naive-ui'
import { computed, inject, onMounted, ref, type ToRefs, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'

import GridItem from '@content-selector/components/ui/GridItem.vue'
import type {
  SelectedFile,
  SelectedFolder,
  SelectedPage,
  RecentFileWithFavoriteId,
  FolderFileWithFolderId,
} from '@content-selector/stores/app'
import { useStore, useStoreApi } from '@content-selector/composables/useStore'
import { createPageId, getFileId, isShareable } from '@content-selector/util/file'
import { isModifierClick } from '@lib/util/navigation.util'
import CFullScreenLoader from '@lib/components/CFullScreenLoader/CFullScreenLoader.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CList from '@lib/components/CList/CList.vue'
import CSearchFilters from '@lib/components/CSearchFilters/CSearchFilters.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import { useElementHeightResize } from '@lib/composables/useElementHeightResize'
import { type FolderFile, type MiniFolder, FileContentTypeEnum } from '@openapi'
import FoldersFilesListItem from './FoldersFilesListItem.vue'
import ContentGridToolbar from '@canvas-builder/components/ui/ContentGrid/ContentGridToolbar.vue'
import type { AppProps } from '@content-selector/appTypes'
import useMetadataTemplates from '@lib/composables/useMetadataTemplates'
import CButton from '@lib/components/CButton/CButton.vue'
import type { InstanceSettings, OrganizationSettings } from '@lib/main.lib'
import CSectionSelectionActions from '@lib/components/CSectionSelectionActions/CSectionSelectionActions.vue'

const themeVars = useThemeVars()
const { t } = useI18n()
const { state: api } = useStoreApi()
const {
  activeFile,
  deselectAllFiles,
  deselectItem,
  deselectPage,
  allowedIds,
  search,
  searchMetadataFilters,
  selectAllFiles,
  selectedFiles,
  selectedPages,
  selectItem,
  setState,
  setSearchQuery,
  setSearchMetadataFilters,
  selectedSection,
  setSelectedSection,
  displayedFiles,
  isLocalSearch,
  setIsLocalSearch,
  filterLocalItems,
  addedItems,
  selectedItems,
  addItems,
  deleteItems,
  isDragNDropEnabled,
} = useStore()

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const disabledSearchContext = computed(() => pitcherSettings.value.disable_browser_search_context ?? false)
const wrapperRef = ref<HTMLElement | null>(null)
const toolbarWrapperRef = ref<HTMLElement | null>(null)
const { wrapperHeight } = useElementHeightResize(toolbarWrapperRef, calculateWrapperHeight)
const metadataFilters = ref<Record<string, any>>({})
const appProps = inject<ToRefs<AppProps>>('appProps')

const searchFiltersRef = ref<InstanceType<typeof CSearchFilters> | null>(null)

const popularityProps = computed(() => ({
  showLatestFilter: false,
}))

const areAllSelected = computed(() =>
  filesToSelect.value.every((item) => selectedFiles.value.some((i) => i.id === item.id)),
)
const areSomeSelected = computed(
  () => filesToSelect.value.some((item) => selectedFiles.value.some((i) => i.id === item.id)) && !areAllSelected.value,
)

const isFetchingContent = computed(
  () =>
    api.isFetchingFavorites ||
    api.isFetchingRecentFiles ||
    api.isFetchingFile ||
    api.isFetchingFolderItems ||
    api.isFetchingListFiles,
)

const filteredDisplayedFiles = computed(() => {
  if (!appProps?.disableNoPptxData?.value) {
    return displayedFiles.value
  }

  return displayedFiles.value.filter((item: any) => {
    // Hide items that don't have pptx_data when disableNoPptxData is true
    if ('metadata' in item && !item?.metadata?.pptx_data) {
      return false
    }
    return true
  })
})

const filesToSelect = computed(() =>
  filteredDisplayedFiles.value.filter((item: any) => !isItemDisabled(item) && item.type === 'file'),
)
const requestFilters = computed(() => ({
  filters: {
    ...(allowedIds.value?.length ? { id__in: allowedIds.value.join(',') } : {}),
  },
  ...(appProps?.onlyAllowContentPdfPages?.value ? { content_type: FileContentTypeEnum.PDF } : {}),
}))

const getTypedItem = (item: any) => item as FolderFile

function calculateWrapperHeight(height: number) {
  return `${(toolbarWrapperRef.value?.parentElement?.offsetHeight || 0) - Math.ceil(height || 0) - 1}px` // 1 is for the border
}

function isItemDisabled(
  item: SelectedFile | SelectedFolder | SelectedPage | RecentFileWithFavoriteId | FolderFileWithFolderId,
) {
  return (
    (appProps?.disableNoSharable?.value && !isShareable(item)) ||
    (appProps?.disableNoPptxData?.value && 'metadata' in item && !item?.metadata?.pptx_data)
  )
}

async function handleSelectItem(item: SelectedFile | SelectedFolder | SelectedPage) {
  if (isItemDisabled(item)) {
    return
  }

  if (item.type === 'folder') {
    openFolder(item)
  } else if (item.type === 'file' || item.type === 'page') {
    if (item.type === 'file' && appProps?.onlyAllowContentPdfPages?.value) {
      expandFile(item)
      return
    }

    const fileOrFolderExists = selectedFiles.value.find((s) => s.id === item.id)

    if (fileOrFolderExists) deselectItem(item)
    else selectItem(item)
  }
}

function previewFile(item: { id: string; [key: string]: any }, e: MouseEvent) {
  api.fileOpener?.({ fileId: getFileId(item), newTab: isModifierClick(e), pageIndex: item.page?.page_index })
}

async function expandFile(file: FolderFile) {
  setState({ activeFile: await api.fileFetcher?.(getFileId(file)), selectedSection: 'all' })
}

function fetchFilesAfterFiltering(filters: Record<string, any>) {
  setSearchMetadataFilters(filters)
  if (selectedSection.value === 'all' && isLocalSearch.value) {
    filterLocalItems(search.value, filters)
  } else if (selectedSection.value === 'all') {
    api.fileListFetcher?.({ filters: requestFilters.value })
  }
}

async function openFolder(folder: MiniFolder) {
  searchFiltersRef.value?.clearSearch()
  await setState({ activeFolder: await api.folderItemsFetcher?.(folder.id), activeFile: null })
  setSelectedSection('all')
}

async function toggleSelectAll() {
  if (areAllSelected.value) deselectAllFiles(filesToSelect.value)
  else selectAllFiles(filesToSelect.value as unknown as SelectedFile[])
}

function handleSearch(searchValue: string, filters?: Record<string, any>) {
  setSearchQuery(searchValue)
  filters && setSearchMetadataFilters(filters)
  if (selectedSection.value === 'all' && isLocalSearch.value) {
    if (searchValue) {
      filterLocalItems(searchValue, searchMetadataFilters.value)
    } else {
      filterLocalItems('', searchMetadataFilters.value)
    }
  } else if (selectedSection.value === 'all') {
    api.fileListFetcher?.({ filters: requestFilters.value })
  }
}

function toggleSearchMode(isLocal: boolean) {
  setIsLocalSearch(isLocal)
  const hasSearchQuery = !!search.value
  const hasActiveFilters = Object.keys(searchMetadataFilters.value).length > 0

  if (hasSearchQuery || hasActiveFilters) {
    if (isLocal) {
      filterLocalItems(search.value, searchMetadataFilters.value)
    } else {
      api.fileListFetcher?.({ filters: requestFilters.value })
    }
  }
}

// Replace the metadata fetching logic with the composable
const { processedMetadata, fetchTemplate } = useMetadataTemplates({
  itemsRef: displayedFiles,
})

onMounted(async () => {
  // Existing onMounted logic
  if (selectedSection.value === 'all') {
    if (!isEmpty(searchMetadataFilters.value)) metadataFilters.value = searchMetadataFilters.value ?? {}
    api.fileListFetcher?.({ filters: requestFilters.value })
  }

  // Fetch metadata templates using our composable
  await fetchTemplate()
})
</script>
